export function isValidEmail(email) {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validateEmailInput(input, errorEl) {
  const email = input.value.trim();
  if (email && !isValidEmail(email)) {
    input.classList.add('input-error');
    if (errorEl) errorEl.classList.remove('hidden');
    return false;
  }
  input.classList.remove('input-error');
  if (errorEl) errorEl.classList.add('hidden');
  return true;
}

export function formatUaPhone(value) {
  let formatted = '+380 ';
  if (value.length > 0) formatted += '(' + value.substring(0, 2);
  if (value.length >= 3) formatted += ') ' + value.substring(2, 5);
  if (value.length >= 6) formatted += '-' + value.substring(5, 7);
  if (value.length >= 8) formatted += '-' + value.substring(7, 9);
  return formatted;
}

const UA_PHONE_PREFIX = '+380 ';

function setPhoneCaretToEnd(input) {
  if (typeof input.setSelectionRange === 'function') {
    const pos = input.value.length;
    input.setSelectionRange(pos, pos);
  }
}

export function ensureUaPhonePrefix(input) {
  if (!input) return;
  if (!input.value || input.value === '+380' || input.value === '+38' || input.value === '+3') {
    input.value = UA_PHONE_PREFIX;
    setPhoneCaretToEnd(input);
  }
}

export function formatPhoneNumber(input) {
  const rawValue = input.value || '';
  const caretPos = typeof input.selectionStart === 'number' ? input.selectionStart : rawValue.length;
  const lastValue = input.dataset.lastValue || '';
  const lastKey = input.dataset.lastKey || '';
  const lastDigits = lastValue.replace(/\D/g, '');
  let rawDigits = rawValue.replace(/\D/g, '');

  const isDeleteKey = lastKey === 'Backspace' || lastKey === 'Delete';
  if (isDeleteKey && rawDigits.length === lastDigits.length) {
    const lastSelectionStart = parseInt(input.dataset.lastSelectionStart || '0', 10);
    let digitIndex = 0;
    if (lastKey === 'Backspace') {
      const before = lastValue.slice(0, Math.max(lastSelectionStart - 1, 0));
      digitIndex = before.replace(/\D/g, '').length;
    } else {
      const before = lastValue.slice(0, lastSelectionStart);
      digitIndex = before.replace(/\D/g, '').length;
    }
    if (digitIndex >= 0 && digitIndex < lastDigits.length) {
      rawDigits = lastDigits.slice(0, digitIndex) + lastDigits.slice(digitIndex + 1);
    }
  }

  if (rawDigits === '') {
    input.value = UA_PHONE_PREFIX;
    setPhoneCaretToEnd(input);
    return;
  }
  if (rawDigits === '380') {
    input.value = UA_PHONE_PREFIX;
    setPhoneCaretToEnd(input);
    return;
  }

  let normalized = rawDigits;
  let prefixRemoved = 0;
  if (normalized.startsWith('380')) {
    normalized = normalized.substring(3);
    prefixRemoved = 3;
  } else if (normalized.startsWith('0')) {
    normalized = normalized.substring(1);
    prefixRemoved = 1;
  }

  normalized = normalized.substring(0, 9);
  if (normalized === '') {
    input.value = UA_PHONE_PREFIX;
    setPhoneCaretToEnd(input);
    return;
  }

  const formatted = formatUaPhone(normalized);
  let digitsBefore = rawValue.slice(0, caretPos).replace(/\D/g, '').length;
  if (prefixRemoved > 0) digitsBefore = Math.max(0, digitsBefore - prefixRemoved);
  if (isDeleteKey && lastKey === 'Backspace' && rawDigits.length === lastDigits.length) {
    digitsBefore = Math.max(0, digitsBefore - 1);
  }
  digitsBefore = Math.min(digitsBefore, normalized.length);
  input.value = formatted;

  if (typeof input.setSelectionRange === 'function') {
    const targetDigitIndex = normalized.length > 0 ? digitsBefore + 3 : digitsBefore;
    let digitsSeen = 0;
    let targetPos = formatted.length;
    if (targetDigitIndex === 0) targetPos = 0;
    else {
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          digitsSeen++;
          if (digitsSeen >= targetDigitIndex) { targetPos = i + 1; break; }
        }
      }
    }
    input.setSelectionRange(targetPos, targetPos);
  }
}

export function initPhoneInputMask(input) {
  if (!input) return;
  input.addEventListener('keydown', (e) => {
    input.dataset.lastKey = e.key;
    input.dataset.lastValue = input.value;
    input.dataset.lastSelectionStart = String(input.selectionStart);
    input.dataset.lastSelectionEnd = String(input.selectionEnd);
  });
  input.addEventListener('input', () => {
    formatPhoneNumber(input);
    input.dataset.lastKey = '';
    input.dataset.lastValue = input.value;
  });
  input.addEventListener('focus', () => {
    input.dataset.lastKey = '';
    input.dataset.lastValue = input.value;
    ensureUaPhonePrefix(input);
  });
  input.addEventListener('blur', () => {
    if (input.value === UA_PHONE_PREFIX) input.value = '';
  });
}

export function isValidPhone(phone) {
  if (!phone) return true;
  return /^\+380 \(\d{2}\) \d{3}-\d{2}-\d{2}$/.test(phone);
}

import { useCallback, useEffect, useRef } from 'react';

export function usePhoneMask(initial = '') {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    initPhoneInputMask(el);
    if (initial) { el.value = initial; formatPhoneNumber(el); }
  }, [initial]);
  const onInput = useCallback((e) => formatPhoneNumber(e.target), []);
  return { ref, onInput };
}
